import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, File, Search } from 'lucide-react';
import { NOTE_TEMPLATES } from './NoteTemplates';

const TemplateCard = ({ template, onSelect }) => (
    <motion.div
        layout
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
        onClick={() => onSelect(template.content)}
        className="dark:bg-slate-800/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6 cursor-pointer group transition-all hover:shadow-lg hover:border-amber-500/50 hover:-translate-y-1"
    >
        <div className="flex items-start gap-4">
            <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg shadow-lg dark:shadow-amber-900/20 shadow-amber-500/10 text-white">
                <template.icon size={24} />
            </div>
            <div>
                <h3 className="text-base font-bold dark:text-white text-slate-900 mb-1 group-hover:text-amber-500 transition-colors">
                    {template.title}
                </h3>
                <p className="text-sm dark:text-slate-400 text-slate-500 leading-snug">
                    {template.description}
                </p>
            </div>
        </div>
    </motion.div>
);

const TemplatePicker = ({ onSelect, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const categories = [...new Set(NOTE_TEMPLATES.map(t => t.category))];
    const filteredTemplates = NOTE_TEMPLATES.filter(template =>
        template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 dark:bg-slate-950/90 bg-slate-500/50 backdrop-blur-sm"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="w-full max-w-4xl max-h-[80vh] flex flex-col dark:bg-slate-900 bg-slate-50 rounded-2xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b dark:border-slate-800 border-slate-200">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-bold dark:text-white text-slate-900">Create a new note</h2>
                            <p className="dark:text-slate-400 text-slate-500 text-sm mt-1">
                                Start from a template or a blank page.
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Search */}
                    <div className="relative mt-4">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search templates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200 rounded-lg dark:text-white text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition"
                        />
                    </div>
                </div>

                {/* Templates Grid */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Blank Note */}
                        <motion.div
                            layout
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                        >
                            <div
                                onClick={() => onSelect('')}
                                className="h-full dark:bg-slate-800/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6 cursor-pointer group transition-all hover:shadow-lg hover:border-sky-500/50 hover:-translate-y-1 flex items-center"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="p-3 bg-gradient-to-br from-sky-500 to-blue-600 rounded-lg shadow-lg dark:shadow-sky-900/20 shadow-sky-500/10 text-white">
                                        <File size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold dark:text-white text-slate-900 mb-1 group-hover:text-sky-500 transition-colors">
                                            Blank Note
                                        </h3>
                                        <p className="text-sm dark:text-slate-400 text-slate-500 leading-snug">
                                            Start with a fresh, empty page.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <AnimatePresence>
                            {filteredTemplates.map(template => (
                                <TemplateCard key={template.id} template={template} onSelect={onSelect} />
                            ))}
                        </AnimatePresence>
                    </div>

                    {filteredTemplates.length === 0 && searchTerm && (
                        <div className="text-center py-12">
                            <p className="dark:text-slate-400 text-slate-500">No templates found for "{searchTerm}".</p>
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

export default TemplatePicker;
